import {
	getAnimalCareSummaryQueryOptions,
	getAnimalQueryKey,
	getAnimalQueryOptions,
	getAnimalReproductionStatusQueryOptions,
	getLivestockAnimalWeightHistoryQueryOptions,
	getLivestockSettingsQueryOptions,
} from "@packages/api-client/hooks/livestockController";
import { Action } from "@packages/shared-types";
import {
	LivestockMeasurementType,
	LivestockOperationType,
} from "@packages/shared-types/livestock";
import {
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Scale, Upload, Users } from "lucide-react";
import {
	type ReactNode,
	type RefObject,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { getLivestockAnimalMarginQueryOptions } from "@/api/livestock/margin";
import { getAnimalVaccinationStatusQueryOptions } from "@/api/livestock/vaccinationStatus";
import { useListMedia } from "@/api/media";
import { AppDate } from "@/components/format";
import { asKnownCategory } from "@/components/livestock/AnimalCategoryBadge";
import { livestockAttentionForAnimalQuery } from "@/components/livestock/attention/livestockAttention";
import { LivestockEntityDelete } from "@/components/livestock/LivestockEntityDelete";
import { ImageUploadDialog } from "@/components/media/ImageUploadDialog";
import { MediaGallery } from "@/components/media/MediaGallery";
import { OfflineActionGuard } from "@/components/OfflineActionGuard";
import { OfflineUnavailable } from "@/components/OfflineUnavailable";
import { buildAnimalRegisteredEvent } from "@/components/registries/animalRegisteredEvent";
import { RecordsSection } from "@/components/registries/RecordsSection";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useHasPermission } from "@/hooks/useAccessControl";
import { findOfflineAnimalRow } from "@/hooks/useAnimalOptions";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useOfflineGuard } from "@/hooks/useOfflineGuard";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { loadUnlessOffline } from "@/lib/offline/loadUnlessOffline";
import { formatMoney } from "@/utils/financial";
import { BulkMoveToHerdDialog } from "../../../-components/BulkMoveToHerdDialog";
import { AnimalMarginBlock } from "../../-components/AnimalMarginBlock";
import { RegisterPicker } from "../../-components/RegisterPicker";
import { RegistriesTab } from "../../-components/RegistriesTab";
import { SellAnimalsDialog } from "../../-components/SellAnimalsDialog";
import { AnimalDaySuggestion } from "./-components/AnimalDaySuggestion";
import { AnimalDetailOffline } from "./-components/AnimalDetailOffline";
import {
	AnimalDetailsList,
	AnimalEditLink,
	useAnimalDetailRows,
	useAnimalExtraRows,
} from "./-components/AnimalDetailsList";
import {
	AnimalBadges,
	AnimalDetailsCard,
	AnimalIdentityCard,
	AnimalQuickRow,
	AnimalQuickRowsCard,
	useAnimalAgeLabel,
} from "./-components/AnimalIdentityCard";
import {
	AnimalMobileEditButton,
	AnimalMobileTopBar,
	AnimalMoreMenu,
} from "./-components/AnimalMobileChrome";
import { AnimalOffspringTable } from "./-components/AnimalOffspringTable";
import { AnimalPedigreeCard } from "./-components/AnimalPedigreeCard";
import { AnimalReproductionCard } from "./-components/AnimalReproductionCard";
import { AnimalStatusCards } from "./-components/AnimalStatusCards";
import { AnimalVaccinationTable } from "./-components/AnimalVaccinationTable";
import { AnimalWeightCard } from "./-components/AnimalWeightCard";
import { SetHerdTargetWeightDialog } from "./-components/SetHerdTargetWeightDialog";

/** Desktop is where the 300px identity rail earns its column; below it the
 *  page becomes the phone layout (handoff 3a/3b), not a squeezed rail. */
const DESKTOP_QUERY = "(min-width: 1024px)";

/**
 * A stacked section a rail row can scroll to. A ref rather than an element id:
 * the row and the section are the same React tree, so there is nothing to look
 * up by name — and a hardcoded id would be a duplicate waiting to happen.
 */
function AnchoredSection({
	sectionRef,
	children,
}: {
	sectionRef?: RefObject<HTMLDivElement | null>;
	children: ReactNode;
}) {
	return (
		<div ref={sectionRef} className="scroll-mt-4">
			{children}
		</div>
	);
}

/** Smooth-scrolls a stacked section into view from its rail row. */
function scrollTo(ref: RefObject<HTMLDivElement | null>) {
	return () =>
		ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/livestock/$establishmentId/animals/$animalId/",
)({
	staticData: { offline: "ready" },
	// Optional `herd` breadcrumb context: set when the user arrived from a herd,
	// so the trail reads Livestock > Herds > <herd> > <animal> instead of the
	// generic animals-list segment. The org route still owns `farm` scope.
	validateSearch: (search: Record<string, unknown>): { herd?: string } => ({
		herd: typeof search.herd === "string" ? search.herd : undefined,
	}),
	loader: ({ context, params }) =>
		loadUnlessOffline(async () => {
			await Promise.all([
				context.queryClient.ensureQueryData(
					getAnimalQueryOptions(params.orgId, params.animalId),
				),
				// Attention items scoped to this animal (fact-grain: its own overdue
				// vaccinations, weighing, calving). Scope is path-intrinsic, so no
				// `?farm=` read is needed. Additive; must not block first paint.
				context.queryClient.ensureQueryData(
					livestockAttentionForAnimalQuery(
						params.orgId,
						{ kind: "single", establishmentId: params.establishmentId },
						params.animalId,
					),
				),
			]);
			// The vaccination panel is warmed with `prefetchQuery`, NOT
			// `ensureQueryData`: the house idiom awaits and rethrows, so a rejected
			// status read would reject this loader and blank the whole animal page
			// before the panel's own non-throwing query ever ran. `prefetchQuery`
			// resolves either way — the panel is additive and must never take the
			// page down with it. Deliberately outside the `Promise.all` above so it
			// cannot delay first paint either.
			void context.queryClient.prefetchQuery(
				getAnimalVaccinationStatusQueryOptions(params.orgId, params.animalId, {
					establishmentId: params.establishmentId,
				}),
			);
			void context.queryClient.prefetchQuery(
				getAnimalCareSummaryQueryOptions(params.orgId, params.animalId),
			);
			void context.queryClient.prefetchQuery(
				getLivestockAnimalWeightHistoryQueryOptions(
					params.orgId,
					params.animalId,
				),
			);
		}),
	component: AnimalDetailsPage,
});

function AnimalDetailsPage() {
	const { orgId, establishmentId, animalId } = Route.useParams();
	const online = useOnlineStatus();
	const queryClient = useQueryClient();

	if (!online) {
		const cachedFull = queryClient.getQueryData(
			getAnimalQueryKey(orgId, animalId),
		);
		if (!cachedFull) {
			const row = findOfflineAnimalRow(
				queryClient,
				orgId,
				establishmentId,
				animalId,
			);
			// Prepared dataset row → offline read-only view; nothing at all →
			// the same "prepare in the Offline Center" panel the guard used to show.
			return row ? (
				<AnimalDetailOffline row={row} />
			) : (
				<OfflineUnavailable variant="preparable" />
			);
		}
	}

	return <AnimalDetailsFull />;
}

/**
 * The animal page, per the Claude Design handoff (options 1b/2a desktop, 3a/3b
 * mobile).
 *
 * Two arrangements of ONE set of sections, chosen by viewport rather than by
 * CSS visibility, so the phone never carries a hidden copy of the desktop tree
 * (duplicate headings, duplicate landmarks, twice the DOM):
 *
 *  - Desktop — a 300px identity rail beside a stack of Weight, Attention/Health,
 *    Vaccination and Records. No tabs: the redesign's core decision was that a
 *    manager should never have to guess which tab holds the number.
 *  - Mobile — one column under a 56px identity bar, the numbers first and the
 *    twelve-row profile last, with Sell / New record pinned to a sticky bar.
 *
 * Margin and the image gallery keep their full sections, stacked below Records
 * and reachable from the rail's demoted rows; the sanitary record folds into
 * the Health card rather than opening a second sanitary surface.
 */
function AnimalDetailsFull() {
	const { orgId, establishmentId, animalId } = Route.useParams();
	const { herd: herdContext } = Route.useSearch();
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const navigate = Route.useNavigate();
	const checkPermission = useHasPermission();
	const canManageLivestock = checkPermission(Action.MANAGE_LIVESTOCK);
	const isDesktop = useMediaQuery(DESKTOP_QUERY);
	// The route guard only blocks navigation while offline; a user who loaded
	// this screen online and then lost connectivity stays on it — so the write
	// (and guarded-route entry) buttons need their own gate.
	const { blocked } = useOfflineGuard();
	const [sellOpen, setSellOpen] = useState(false);
	const [moveHerdOpen, setMoveHerdOpen] = useState(false);
	const [setTargetOpen, setSetTargetOpen] = useState(false);
	const [showExtraFacts, setShowExtraFacts] = useState(false);
	const marginRef = useRef<HTMLDivElement>(null);
	const imagesRef = useRef<HTMLDivElement>(null);
	const sellMode = useMemo(
		() => ({ kind: "single" as const, animalId }),
		[animalId],
	);

	const { data: animal } = useSuspenseQuery(
		getAnimalQueryOptions(orgId, animalId),
	);

	const { data: mediaData } = useListMedia(orgId, {
		entityType: "LivestockAnimal",
		entityId: animalId,
	});
	const imagesCount = mediaData?.data?.length ?? 0;
	/**
	 * The animal's ALL-TIME record count. Deliberately not the filtered feed's
	 * length: the Records feed defaults to the last 12 months, which can be empty
	 * for an animal that does have older records.
	 */
	const totalRecords =
		(animal.operationCount ?? 0) +
		(animal.measurementCount ?? 0) +
		(animal.movementCount ?? 0);
	const category = asKnownCategory(animal.category);
	// Age rides beside the brinco on both layouts — the desktop rail reads it
	// from the same hook inside the identity card.
	const ageLabel = useAnimalAgeLabel(animal.birthDate);
	const hasNotes = Boolean(animal.notes?.trim());
	const death = animal.status === "DECEASED" ? animal.death : undefined;
	/** Nothing recorded at all — the page switches to its day-one shape. */
	const isDayOne = totalRecords === 0;
	const isActive = animal.status === "ACTIVE";
	const canWrite = canManageLivestock && isActive;
	/**
	 * The animal's own registration, as the oldest row of its Records timeline.
	 * Derived from `createdAt`, so it is not a record: it stays out of
	 * `totalRecords`, the section count and the CSV export, and only exists so a
	 * fresh animal opens on its genesis rather than an empty feed.
	 */
	const registrationEvent = useMemo(
		() => buildAnimalRegisteredEvent(animal, t),
		[animal, t],
	);

	// Expected-calving projection (Phase 3): derived server-side from the cow's
	// breeding/diagnosis records — only worth fetching for an active female.
	const { data: reproduction } = useQuery({
		...getAnimalReproductionStatusQueryOptions(orgId, animalId),
		enabled: animal?.sex === "FEMALE" && animal?.status === "ACTIVE",
	});
	const expectedCalvingDate =
		reproduction?.sex === "FEMALE" && reproduction.status === "PREGNANT"
			? reproduction.expectedCalvingDate
			: undefined;

	// Weight-history headline (arrobas, carcass, target, lot rank, the curve).
	// Non-throwing so a failed KPI never blanks the page. Prefetched in the loader.
	const { data: livestockSettings } = useQuery(
		getLivestockSettingsQueryOptions(orgId),
	);

	const { data: weightHistory } = useQuery({
		...getLivestockAnimalWeightHistoryQueryOptions(orgId, animalId),
		throwOnError: false,
	});
	// Same key the margin block reads (no what-if override), so the summary
	// row and the full block never disagree about whether a margin exists.
	const { data: marginSummary } = useQuery({
		...getLivestockAnimalMarginQueryOptions(orgId, animalId),
		throwOnError: false,
	});

	// Attention items scoped to this animal (fact-grain redesign). Prefetched in
	// the loader, but non-suspense + non-throwing here: the section is additive
	// and must never blank the page if the attention read fails.
	const { data: attentionData } = useQuery({
		...livestockAttentionForAnimalQuery(
			orgId,
			{ kind: "single", establishmentId },
			animalId,
		),
		throwOnError: false,
	});
	const attentionItems = attentionData?.items ?? [];

	// The animal's standing against every configured vaccination rule — the
	// not-yet-due half the attention feed deliberately cannot carry. Prefetched
	// non-throwingly in the loader; non-suspense and non-throwing here too, so a
	// failed read leaves the section unrendered instead of blanking the page.
	const { data: vaccinationStatus } = useQuery({
		...getAnimalVaccinationStatusQueryOptions(orgId, animalId, {
			establishmentId,
		}),
		throwOnError: false,
	});

	// The care summary feeds two things: the Health card's one-line standing and
	// the Sell flow's withdrawal block. One read, non-throwing for the same
	// reason as the others.
	const { data: careSummary } = useQuery({
		...getAnimalCareSummaryQueryOptions(orgId, animalId),
		throwOnError: false,
	});
	const sellWithdrawal = careSummary?.withdrawal?.blocksSale
		? {
				untilDate: careSummary.withdrawal.untilDate,
				medication: careSummary.withdrawal.medication,
			}
		: undefined;

	const moveHerdAction = canWrite ? (
		<OfflineActionGuard blocked={blocked}>
			<button
				type="button"
				aria-disabled={blocked}
				className="shrink-0 text-xs font-semibold text-primary hover:underline"
				onClick={() => setMoveHerdOpen(true)}
			>
				{t("livestock.animal.moveHerd")}
			</button>
		</OfflineActionGuard>
	) : undefined;

	const { rows, filled, total } = useAnimalDetailRows({
		animal,
		moveHerdAction,
	});
	const extraRows = useAnimalExtraRows(animal);
	const editLink = canManageLivestock
		? (label: string) => (
				<AnimalEditLink
					orgId={orgId}
					establishmentId={establishmentId}
					animalId={animalId}
					blocked={blocked}
					label={label}
				/>
			)
		: undefined;

	if (!animal) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">{t("livestock.animal.notFound")}</p>
			</div>
		);
	}

	// --- Shared building blocks ---------------------------------------------
	// Built once and placed by whichever arrangement is active. Anything that
	// needs a write path is rendered through the page's single owner of that
	// path (RegisterPicker, ImageUploadDialog, the two dialogs at the bottom).

	const goToEdit = () =>
		navigate({
			to: "/app/org/$orgId/livestock/$establishmentId/animals/$animalId/edit",
			params: { orgId, establishmentId, animalId },
		});

	const goAfterDelete = () => {
		if (herdContext && animal.herd) {
			void navigate({
				to: "/app/org/$orgId/livestock/$establishmentId/herds/$herdId",
				params: {
					orgId,
					establishmentId,
					herdId: animal.herd.id,
				},
			});
			return;
		}
		void navigate({
			to: "/app/org/$orgId/livestock",
			params: { orgId },
			search: { tab: "animals", farm: establishmentId },
		});
	};

	const animalDeleteMenuItem = canManageLivestock ? (
		<LivestockEntityDelete
			orgId={orgId}
			kind="animal"
			entityId={animalId}
			entityName={animal.tagId}
			blocked={blocked}
			variant="menu-item"
			onDeleted={goAfterDelete}
		/>
	) : null;

	const newRecordPicker = (
		trigger: (open: () => void) => ReactNode,
	): ReactNode => (
		<RegisterPicker
			orgId={orgId}
			establishmentId={establishmentId}
			scope={{ animalId, animalSex: animal.sex }}
			blocked={blocked}
			trigger={(open) => trigger(() => open())}
		/>
	);

	const weighAction = canWrite ? (
		<RegisterPicker
			orgId={orgId}
			establishmentId={establishmentId}
			scope={{ animalId, animalSex: animal.sex }}
			blocked={blocked}
			trigger={(open) => (
				<OfflineActionGuard blocked={blocked}>
					<Button
						type="button"
						size="sm"
						aria-disabled={blocked}
						onClick={() =>
							open({
								kind: "measurement",
								type: LivestockMeasurementType.WEIGHT,
							})
						}
					>
						<Plus className="mr-2 h-4 w-4" aria-hidden />
						{t("livestock.animal.detail.weight.add")}
					</Button>
				</OfflineActionGuard>
			)}
		/>
	) : undefined;

	const uploadButton = (
		<OfflineActionGuard blocked={blocked}>
			<ImageUploadDialog
				orgId={orgId}
				entityType="LivestockAnimal"
				entityId={animalId}
				trigger={
					<Button
						type="button"
						variant="outline"
						size="sm"
						aria-disabled={blocked}
					>
						<Upload className="mr-2 h-4 w-4" aria-hidden />
						{t("media.upload.button")}
					</Button>
				}
			/>
		</OfflineActionGuard>
	);

	const weightCard = (
		<AnimalWeightCard
			dryMatterIntakePercent={livestockSettings?.dryMatterIntakePercent}
			waterIntakePercent={livestockSettings?.waterIntakePercent}
			history={weightHistory}
			weighAction={weighAction}
		/>
	);

	const statusCards = (
		<AnimalStatusCards
			orgId={orgId}
			animalId={animalId}
			attentionItems={attentionItems}
			careSummary={careSummary}
			vaccinationItems={vaccinationStatus?.items}
			dayOne={isDayOne}
			scheduleFirstWeighing={
				weighAction ? (
					<RegisterPicker
						orgId={orgId}
						establishmentId={establishmentId}
						scope={{ animalId, animalSex: animal.sex }}
						blocked={blocked}
						trigger={(open) => (
							<button
								type="button"
								className="font-semibold text-primary hover:underline"
								onClick={() =>
									open({
										kind: "measurement",
										type: LivestockMeasurementType.WEIGHT,
									})
								}
							>
								{t("livestock.animal.detail.attention.scheduleFirstWeighing")}
							</button>
						)}
					/>
				) : undefined
			}
			layout={isDesktop ? "grid" : "stacked"}
		/>
	);

	const vaccinationCard = vaccinationStatus?.items !== undefined && (
		<AnimalVaccinationTable
			orgId={orgId}
			items={vaccinationStatus.items}
			compact={!isDesktop}
			showFooterNote={isDayOne}
			recordAction={
				canWrite ? (
					<RegisterPicker
						orgId={orgId}
						establishmentId={establishmentId}
						scope={{ animalId, animalSex: animal.sex }}
						blocked={blocked}
						trigger={(open) => (
							<OfflineActionGuard blocked={blocked}>
								<Button
									type="button"
									variant={isDesktop ? "outline" : "ghost"}
									size="sm"
									aria-disabled={blocked}
									onClick={() =>
										open({
											kind: "operation",
											type: LivestockOperationType.VACCINATION,
										})
									}
								>
									<Plus className="mr-2 h-4 w-4" aria-hidden />
									{t("livestock.animal.detail.vaccination.record")}
								</Button>
							</OfflineActionGuard>
						)}
					/>
				) : undefined
			}
		/>
	);

	const recordsCard = (
		<AnchoredSection>
			<RecordsSection
				title={t("livestock.animal.detail.records.title")}
				count={totalRecords}
				hint={t("livestock.animal.detail.records.hint")}
				action={
					canWrite &&
					newRecordPicker((open) => (
						<OfflineActionGuard blocked={blocked}>
							<Button
								type="button"
								size="sm"
								aria-disabled={blocked}
								onClick={open}
							>
								<Plus className="mr-2 h-4 w-4" aria-hidden />
								{t("livestock.registries.register.button")}
							</Button>
						</OfflineActionGuard>
					))
				}
			>
				<RegistriesTab
					orgId={orgId}
					establishmentId={establishmentId}
					scope={{ animalId }}
					totalRecords={totalRecords}
					hideFirstUseCtas
					registrationEvent={registrationEvent}
				/>
			</RecordsSection>
		</AnchoredSection>
	);

	/** Everything the handoff demoted or never saw, in one stacked tail. */
	const stackedSections = (
		<>
			<AnimalPedigreeCard
				orgId={orgId}
				establishmentId={establishmentId}
				animalId={animalId}
			/>
			{/* Reproductive standing: every female has a cycle/productivity read;
			    only an adult intact male (the canonical TOURO category) gets the
			    bull branch. Additive and non-throwing inside the card. */}
			{(animal.sex === "FEMALE" || category === "TOURO") && (
				<AnimalReproductionCard
					orgId={orgId}
					establishmentId={establishmentId}
					animalId={animalId}
				/>
			)}
			{/* The bull card's progeny link jumps forward to this additive table. */}
			{(animal.offspringCount ?? 0) > 0 && (
				<AnimalOffspringTable
					orgId={orgId}
					establishmentId={establishmentId}
					animalId={animalId}
					count={animal.offspringCount ?? 0}
				/>
			)}
			<AnchoredSection sectionRef={marginRef}>
				<AnimalMarginBlock orgId={orgId} animalId={animalId} />
			</AnchoredSection>
			<AnchoredSection sectionRef={imagesRef}>
				<Card>
					<CardContent className="p-4 pt-4 sm:p-6 sm:pt-6">
						<MediaGallery
							orgId={orgId}
							entityType="LivestockAnimal"
							entityId={animalId}
							emptyText={t("media.gallery.emptyWithHint")}
							emptyAction={uploadButton}
						/>
					</CardContent>
				</Card>
			</AnchoredSection>
		</>
	);

	const quickRows = (
		<>
			<AnimalQuickRow
				title={t("livestock.margin.title")}
				caption={
					marginSummary?.sold && marginSummary.margin
						? t("livestock.animal.detail.margin.realized", {
								value: formatMoney(marginSummary.margin.netMargin),
							})
						: marginSummary?.projection
							? t("livestock.animal.detail.margin.projected", {
									value: formatMoney(marginSummary.projection.projectedMargin),
									price: formatMoney(
										marginSummary.projection.referencePricePerArroba,
									),
								})
							: marginSummary?.referencePrice &&
									marginSummary.hasWeighing === false
								? t("livestock.animal.detail.margin.needsWeighing")
								: t(
										animal.acquisitionCost != null
											? "livestock.animal.detail.margin.open"
											: animal.originType === "born_on_farm"
												? "livestock.animal.detail.margin.noCost"
												: "livestock.animal.detail.margin.addCost",
									)
				}
				onOpen={scrollTo(marginRef)}
			/>
			<AnimalQuickRow
				title={t("livestock.animal.detail.images.title", {
					count: imagesCount,
				})}
				caption={
					imagesCount === 0
						? t("livestock.animal.detail.images.none")
						: t("media.gallery.title")
				}
				action={
					imagesCount === 0 && canManageLivestock ? uploadButton : undefined
				}
				onOpen={scrollTo(imagesRef)}
			/>
		</>
	);

	/** The recorded facts the twelve-row list does not carry, behind a toggle. */
	const extraFacts = extraRows.length > 0 && (
		<div>
			<button
				type="button"
				onClick={() => setShowExtraFacts((open) => !open)}
				aria-expanded={showExtraFacts}
				className="text-[13px] font-semibold text-primary hover:underline"
			>
				{t(showExtraFacts ? "common.showLess" : "common.showMore")}
			</button>
			{showExtraFacts && (
				<div className="mt-2.5">
					<AnimalDetailsList rows={extraRows} />
				</div>
			)}
		</div>
	);

	const profileFooter = (
		<>
			{filled < total && (
				<p className="text-[13px] text-base-content/60">
					{t("livestock.animal.detail.details.fieldsFilled", {
						filled,
						total,
					})}{" "}
					{editLink?.(t("livestock.animal.detail.details.completeDetails"))}
				</p>
			)}
			{extraFacts}
			{hasNotes && (
				<div>
					<div className="text-xs font-medium text-base-content/60">
						{t("livestock.animal.notes")}
					</div>
					<p className="mt-1 whitespace-pre-line text-sm text-base-content">
						{animal.notes}
					</p>
				</div>
			)}
			{expectedCalvingDate && (
				<p className="text-[13px] text-base-content/60">
					{t("livestock.timeline.expectedCalvingDate")}:{" "}
					<span className="font-semibold text-base-content">
						<AppDate value={expectedCalvingDate} />
					</span>
				</p>
			)}
		</>
	);

	/* Death outranks everything else on the page once the animal is gone. */
	const deathBanner = death && (
		<div
			role="alert"
			className="rounded-box border border-error/30 bg-error/5 p-4"
		>
			<div className="text-[11px] font-bold uppercase tracking-wider text-error">
				{t("livestock.animal.death.title")}
			</div>
			<p className="mt-1 text-sm text-base-content">
				<AppDate value={death.date} />
				{" · "}
				{death.cause ?? t("livestock.animal.death.causeUnknown")}
			</p>
		</div>
	);

	const suggestion = isDayOne && canWrite && (
		<AnimalDaySuggestion
			orgId={orgId}
			establishmentId={establishmentId}
			animalId={animalId}
			tagId={animal.tagId}
			sex={animal.sex}
			blocked={blocked}
		/>
	);

	const moreMenu = canManageLivestock ? (
		<AnimalMoreMenu>
			{canWrite && (
				<>
					<ImageUploadDialog
						orgId={orgId}
						entityType="LivestockAnimal"
						entityId={animalId}
						trigger={
							<Button
								type="button"
								variant="ghost"
								size="touch"
								className="justify-start"
							>
								<Upload className="mr-2 h-4 w-4" aria-hidden />
								{t("media.upload.button")}
							</Button>
						}
					/>
					<Button
						type="button"
						variant="ghost"
						size="touch"
						className="justify-start"
						onClick={() => setMoveHerdOpen(true)}
					>
						<Users className="mr-2 h-4 w-4" aria-hidden />
						{t("livestock.animal.moveHerd")}
					</Button>
					{animal.herd && (
						<Button
							type="button"
							variant="ghost"
							size="touch"
							className="justify-start"
							onClick={() => setSetTargetOpen(true)}
						>
							<Scale className="mr-2 h-4 w-4" aria-hidden />
							{t("livestock.animal.setTargetTitle")}
						</Button>
					)}
				</>
			)}
			{animalDeleteMenuItem}
		</AnimalMoreMenu>
	) : undefined;

	const dialogs = (
		<>
			<SellAnimalsDialog
				open={sellOpen}
				onOpenChange={setSellOpen}
				orgId={orgId}
				establishmentId={establishmentId}
				mode={sellMode}
				withdrawal={sellWithdrawal}
			/>
			<BulkMoveToHerdDialog
				open={moveHerdOpen}
				onOpenChange={setMoveHerdOpen}
				orgId={orgId}
				establishmentId={establishmentId}
				animals={[{ id: animalId, tagId: animal.tagId }]}
				onCompleted={() => {
					setMoveHerdOpen(false);
					queryClient.invalidateQueries({
						queryKey: getAnimalQueryKey(orgId, animalId),
					});
				}}
			/>
			{animal.herd && (
				<SetHerdTargetWeightDialog
					open={setTargetOpen}
					onOpenChange={setSetTargetOpen}
					orgId={orgId}
					animalId={animalId}
					herdId={animal.herd.id}
					herdName={animal.herd.name}
					currentTargetKg={weightHistory?.target?.weightKg}
				/>
			)}
		</>
	);

	// --- Desktop (handoff 1b / 2a) ------------------------------------------
	if (isDesktop) {
		return (
			// The shell is height-locked (`html,body{overflow:hidden}` + an
			// `h-[100dvh]` wrapper), so the page owns its scroll.
			<div className="flex h-full flex-col gap-5 overflow-y-auto pb-10">
				<div className="flex items-center justify-between gap-4">
					<Breadcrumb
						items={[
							{
								label: t("livestock.nav.title"),
								to: "/app/org/$orgId/livestock/$establishmentId",
								params: { orgId, establishmentId },
							},
							// Herd context (item 35): if the user came from a herd and the
							// animal still belongs to one, trail through Herds > <herd>;
							// otherwise the plain animals-list segment.
							...(herdContext && animal.herd
								? [
										{
											label: t("livestock.herd.list.title"),
											to: "/app/org/$orgId/livestock",
											params: { orgId },
											search: { tab: "herds", farm: establishmentId },
										},
										{
											label: animal.herd.name,
											to: "/app/org/$orgId/livestock/$establishmentId/herds/$herdId",
											params: {
												orgId,
												establishmentId,
												herdId: animal.herd.id,
											},
										},
									]
								: [
										{
											label: t("livestock.animal.list.title"),
											to: "/app/org/$orgId/livestock",
											params: { orgId },
											search: { tab: "animals", farm: establishmentId },
										},
									]),
							{ label: animal.tagId },
						]}
					/>
					{/* Edit and Sell stay up here; Record lives on the Records
					    section that owns the write. More is the same overflow
					    the phone header uses (upload, move herd, set target,
					    delete). */}
					<div className="flex shrink-0 items-center gap-2.5">
						{canManageLivestock && (
							<OfflineActionGuard blocked={blocked}>
								<Button
									type="button"
									variant="outline"
									size="touch"
									aria-disabled={blocked}
									onClick={goToEdit}
								>
									{t("common.edit")}
								</Button>
							</OfflineActionGuard>
						)}
						{canWrite && (
							<OfflineActionGuard blocked={blocked}>
								<Button
									type="button"
									variant="accent"
									size="touch"
									aria-disabled={blocked}
									onClick={() => setSellOpen(true)}
								>
									{t("livestock.sales.sellCta")}
								</Button>
							</OfflineActionGuard>
						)}
						{moreMenu}
					</div>
				</div>

				{deathBanner}
				{suggestion}

				<div className="grid grid-cols-[300px_minmax(0,1fr)] items-start gap-5">
					<AnimalIdentityCard
						animal={animal}
						category={category}
						dayOne={isDayOne}
						rows={rows}
						editLink={editLink}
						footer={profileFooter}
						quickRows={quickRows}
					/>
					<div className="flex min-w-0 flex-col gap-5">
						{weightCard}
						{statusCards}
						{vaccinationCard}
						{recordsCard}
						{stackedSections}
					</div>
				</div>
				{dialogs}
			</div>
		);
	}

	// --- Mobile (handoff 3a / 3b) -------------------------------------------
	// The bar's sub-line is the lote alone; sex and breed ride the badge row
	// below, where they can wrap instead of being truncated away.
	const subtitle =
		animal.herd?.name ??
		(isDayOne
			? t("livestock.animal.detail.badges.registeredToday").toLowerCase()
			: "");

	return (
		<div className="flex h-full flex-col overflow-y-auto">
			<AnimalMobileTopBar
				title={animal.tagId}
				titleAside={ageLabel ?? undefined}
				subtitle={subtitle}
				onBack={() => window.history.back()}
				editAction={
					canManageLivestock ? (
						<OfflineActionGuard blocked={blocked}>
							<AnimalMobileEditButton onClick={goToEdit} blocked={blocked} />
						</OfflineActionGuard>
					) : null
				}
				moreMenu={moreMenu}
			/>

			{/* No sticky action bar to clear; `<main>` already reserves the
			    floating navigation pill's own height below the page. */}
			<div className="flex flex-col gap-3.5 pb-4 pt-4">
				<AnimalBadges
					animal={animal}
					category={category}
					dayOne={isDayOne}
					withIdentity
				/>
				{deathBanner}
				{suggestion}
				{weightCard}
				{statusCards}
				{vaccinationCard}
				{recordsCard}
				<AnimalDetailsCard
					rows={rows}
					editLink={editLink}
					filledLabel={
						filled < total
							? t("livestock.animal.detail.details.fieldsFilledShort", {
									filled,
									total,
								})
							: undefined
					}
					action={
						<>
							{profileFooter}
							{filled < total && canManageLivestock && (
								<AnimalEditLink
									orgId={orgId}
									establishmentId={establishmentId}
									animalId={animalId}
									blocked={blocked}
									label={t("livestock.animal.detail.details.completeDetails")}
									className="inline-flex h-11 w-full items-center justify-center rounded-md border border-base-300 text-sm font-medium text-base-content"
								/>
							)}
						</>
					}
				/>
				<AnimalQuickRowsCard>{quickRows}</AnimalQuickRowsCard>
				{stackedSections}
			</div>

			{dialogs}
		</div>
	);
}
